import React, { createContext, useRef, useState } from 'react';
import useToggle from './_hooks/use-toggle';

const AUTO_HIDE_TIMEOUT = 3000;

export interface PlayerControls {
  handlePlayerMouseMove: () => void;
  handlePlayerMouseLeave: () => void;
  handlePlayerMouseEnter: () => void;
  disablePlayerMouseMove: () => void;
  enablePlayerMouseMove: () => void;
  fullscreen: boolean;
  closeControls: () => void;
  openControls: () => void;
  handlePlayerControlsMouseEnter: () => void;
  handlePlayerControlsMouseLeave: () => void;
  controlsOpened: boolean;
  handleToggleFullscreen: () => void;
  onPause: (() => void) | null;
  setOnPause: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  onPlay: (() => void) | null;
  setOnPlay: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  handleBeginStartOver: (() => void) | null;
  setHandleBeginStartOver: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  handleCancelStartOver: (() => void) | null;
  setHandleCancelStartOver: React.Dispatch<React.SetStateAction<(() => void) | null>>;
}

interface PlayerControlsProps {
  children: React.ReactNode | React.ReactNode[];
}

export const PlayerControlsContext = createContext<PlayerControls>({} as PlayerControls);

export const PlayerControlsConsumer = PlayerControlsContext.Consumer;

export const PlayerControlsProvider = (props: PlayerControlsProps): any => {
  const { children } = props;

  const autoHideTimeout = useRef<number | null>(null);

  const [opened, setOpened] = useState(false);

  const [hovered, setHovered] = useState(false);

  const [fullscreen, toggleFullscreen] = useToggle(false);

  const [playerMouseMoveDisabled, setPlayerMouseMoveDisabled] = React.useState(false);

  const [onPause, setOnPause] = useState<(() => void) | null>(null);
  const [onPlay, setOnPlay] = useState<(() => void) | null>(null);
  const [handleBeginStartOver, setHandleBeginStartOver] = useState<(() => void) | null>(null);
  const [handleCancelStartOver, setHandleCancelStartOver] = useState<(() => void) | null>(null);

  const handleClearAutoHideTimeout = React.useCallback(() => {
    if (autoHideTimeout.current) {
      clearTimeout(autoHideTimeout.current);
      autoHideTimeout.current = null;
    }
  }, []);

  const closeControls = React.useCallback(() => {
    setOpened(false);

    handleClearAutoHideTimeout();
  }, [handleClearAutoHideTimeout]);

  const handleSetAutoHideTimeout = React.useCallback(() => {
    autoHideTimeout.current = window.setTimeout(() => {
      if (!hovered) {
        closeControls();
      }
    }, AUTO_HIDE_TIMEOUT);
  }, [closeControls, hovered]);

  const openControls = React.useCallback(() => {
    setOpened(true);

    handleClearAutoHideTimeout();

    handleSetAutoHideTimeout();
  }, [handleClearAutoHideTimeout, handleSetAutoHideTimeout]);

  const disablePlayerMouseMove = React.useCallback(() => {
    setPlayerMouseMoveDisabled(true);
  }, []);

  const enablePlayerMouseMove = React.useCallback(() => {
    setPlayerMouseMoveDisabled(false);
  }, []);

  const handlePlayerMouseEnter = React.useCallback(() => {
    openControls();
  }, [openControls]);

  const handlePlayerMouseMove = React.useCallback(() => {
    if (playerMouseMoveDisabled) return;

    openControls();
  }, [openControls, playerMouseMoveDisabled]);

  const handlePlayerMouseLeave = React.useCallback(() => {
    closeControls();
  }, [closeControls]);

  const handlePlayerControlsMouseEnter = React.useCallback(() => {
    setHovered(true);
  }, []);

  const handlePlayerControlsMouseLeave = React.useCallback(() => {
    setHovered(false);
  }, []);

  const handleToggleFullscreen = React.useCallback(() => {
    toggleFullscreen();
  }, [toggleFullscreen]);

  return (
    <PlayerControlsContext.Provider
      value={{
        controlsOpened: opened,
        handlePlayerMouseMove,
        handlePlayerMouseLeave,
        handlePlayerMouseEnter,
        closeControls,
        openControls,
        disablePlayerMouseMove,
        enablePlayerMouseMove,
        fullscreen,
        handleToggleFullscreen,
        handlePlayerControlsMouseEnter,
        handlePlayerControlsMouseLeave,
        onPause,
        setOnPause,
        onPlay,
        setOnPlay,
        handleBeginStartOver,
        setHandleBeginStartOver,
        handleCancelStartOver,
        setHandleCancelStartOver
      }}
    >
      {children}
    </PlayerControlsContext.Provider>
  );
};

export default PlayerControlsProvider;
